import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';

const MetricasCard = ({ 
  titulo, 
  valor, 
  icon, 
  cor, 
  formato = 'numero',
  tendencia = null,
  porcentagem = null,
  carregando = false,
  customTheme 
}) => {
  const theme = useTheme();

  // Função para formatar o valor baseado no tipo
  const formatarValor = (val, fmt) => {
    if (carregando) return '-';
    
    switch (fmt) {
      case 'tempo':
        if (typeof val === 'string') return val;
        return val ? `${val} min` : '0 min';
      case 'moeda':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(val || 0);
      case 'porcentagem':
        return `${(val || 0).toFixed(1)}%`;
      case 'numero':
      default:
        return (val || 0).toLocaleString('pt-BR');
    }
  };

  // Ícone de tendência
  const renderTendenciaIcon = () => {
    if (!tendencia || tendencia === 0) return <RemoveIcon sx={{ fontSize: 16 }} />;
    return tendencia > 0 ? 
      <TrendingUpIcon sx={{ fontSize: 16, color: theme.palette.success.main }} /> :
      <TrendingDownIcon sx={{ fontSize: 16, color: theme.palette.error.main }} />;
  };

  return (
    <Card 
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'visible',
        position: 'relative',
        background: theme.palette.mode === 'dark' 
          ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.8)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.1)} 100%)`,
        border: `1px solid ${alpha(cor, 0.2)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 24px ${alpha(cor, 0.25)}`,
          border: `1px solid ${alpha(cor, 0.4)}`,
        }
      }}
    >
      {/* Barra de progresso animada no topo */}
      {porcentagem !== null && (
        <LinearProgress
          variant="determinate"
          value={porcentagem}
          sx={{
            height: 4,
            backgroundColor: alpha(cor, 0.1),
            '& .MuiLinearProgress-bar': {
              backgroundColor: cor,
              transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
            }
          }}
        />
      )}

      <CardContent sx={{ p: 3, position: 'relative' }}>
        {/* Ícone de fundo decorativo */}
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            right: -10,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${alpha(cor, 0.1)} 0%, ${alpha(cor, 0.05)} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.3,
          }}
        >
          <Box sx={{ fontSize: '2rem', color: cor }}>
            {icon}
          </Box>
        </Box>

        {/* Conteúdo principal */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {/* Cabeçalho com ícone e título */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(cor, 0.15)} 0%, ${alpha(cor, 0.05)} 100%)`,
                color: cor,
                mr: 2,
                boxShadow: `0 4px 12px ${alpha(cor, 0.15)}`,
              }}
            >
              {icon}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              {titulo}
            </Typography>
          </Box>

          {/* Valor principal */}
          <Box sx={{ mb: 2 }}>
            {carregando ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: cor }} />
                <Typography variant="h4" sx={{ color: 'text.secondary' }}>
                  Carregando...
                </Typography>
              </Box>
            ) : (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.75rem', sm: '2rem' },
                  lineHeight: 1.2,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${cor} 100%)`
                    : `linear-gradient(135deg, ${cor} 0%, ${alpha(cor, 0.8)} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {formatarValor(valor, formato)}
              </Typography>
            )}
          </Box>

          {/* Indicador de tendência */}
          {tendencia !== null && !carregando && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {renderTendenciaIcon()}
              <Typography
                variant="caption"
                sx={{
                  color: tendencia > 0 ? 'success.main' : tendencia < 0 ? 'error.main' : 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem'
                }}
              >
                {tendencia > 0 ? '+' : ''}{tendencia?.toFixed(1)}% em relação ao período anterior
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricasCard; 